import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, Menu, X } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import { organizationSchema } from '../components/seo/SchemaMarkup';
import { ProductNavIcon } from '../components/redesign/icons';
import { productDropdownLinks, resourceDropdownLinks } from '../components/redesign/data';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PaperCta from '../components/PaperCta';
import V2PricingCards from '../components/redesign/V2PricingCards';
import './HomePage.css';

const PAPER_ASSET = 'https://app.paper.design/file-assets/01KJMJX949QFE2956T0Y37HAN1';

const HERO_IMAGE = `${PAPER_ASSET}/31W09H4ZBW5YF4JJ4VKAJR9NT6.webp`;

const PRODUCT_CARDS = [
  {
    label: 'Workflows',
    title: 'Automate every workflow',
    body: 'Turn repeatable tasks into agent-run workflows across your tools.',
  },
  {
    label: 'AI staff members',
    title: 'Your own agentic stuff',
    body: "Launch an AI teammate that handles repetitive work across your team's tools.",
    image: `${PAPER_ASSET}/65HJFYWACGYCH1TH7QCV0GGJRT.webp`,
  },
  {
    label: 'Reports',
    title: 'Reports in hand',
    body: 'Generate clear reports from live workflows, conversations, and team activity.',
  },
];

const CAPABILITY_CARDS = [
  ['Lead Scout', 'Find potential clients, qualify leads, and prepare outreach lists for your sales team.', '/leadscout.png'],
  ['Outreach Agent', 'Draft cold emails, send outreach sequences, and follow up until prospects reply.', '/outreach-agent-v2.png'],
  ['Research Assistant', 'Research markets, competitors, and documents, then turn findings into clear briefs.', '/research.png'],
  ['Support Triage', 'Route customer issues, organize requests, and help deliver seamless support.', `${PAPER_ASSET}/5DEKSH5WAR8XV0AV2B594N1QYE.webp`],
  ['A Virtual Assistant', 'Handle tasks, follow-ups, meetings, schedules, reminders, and admin work.', `${PAPER_ASSET}/7P5Q47FRGSJHHJXHA0NZXB3V0V.webp`],
  ['Finance Assistant', 'Guide money decisions, track inflows and outflows, review spending, and flag next steps.', `${PAPER_ASSET}/228Z293FPK20ESM7TWN5B0QJ7N.webp`],
];

const LEARNING_STEPS = [
  ['1', 'Train one agent for one job', 'Give each agent its own playbook, memory, examples, tools, rules, and job-specific instructions.', '/agent-learn-step-2.png'],
  ['2', 'Control the access it gets', 'Connect only the apps, accounts, and websites each agent needs, with access contained to its role.', '/agent-learn-step-1.png'],
  ['3', 'Turn agents into workflow apps', 'Let agents draft, research, and build workflow interfaces where your team can review and approve.', '/agent-learn-step-3-v2.png'],
  ['4', 'Run and share anywhere', 'Move agents between local and cloud environments, then share templates without sharing credentials.', '/agent-learn-step-3.png'],
];

const BLOG_CARDS = [
  ['What is OpenClaw?', 'A practical guide to the agent framework powering always-on AI teammates.', '/blog/what-is-openclaw', '/openclaw-blog-card.png'],
  ['Deploy your first AI agent', 'Go from idea to a live agent with the setup path your team can actually follow.', '/blog/deploy-first-ai-agent', '/research.png'],
  ['AI agents vs assistants', 'Understand the difference between simple assistants and agents that can run workflows.', '/blog/ai-agents-vs-assistants', '/futurism-ai-employees-chaos.jpg'],
  ['Five practical AI agent use cases', 'See where agentic staff can help support, research, productivity, and operations.', '/blog/five-use-cases-ai-agents', '/blog-ai-usecases-folder.png'],
  ['AI agent security best practices', 'Learn how to keep agents, credentials, access, and workflows safer in production.', '/blog/ai-agent-security-best-practices', `${PAPER_ASSET}/509WS22513RSTKG9YAZ4PFPFPD.webp`],
];

const TRUST_LOGOS = [
  { label: 'Microsoft For Startups', image: '/microsoft-for-startups.png' },
  { label: 'OpenAI', image: '/openai-logo.png' },
  { label: 'Daytona', image: '/daytona.svg', compact: true, large: true },
  { label: 'Hatch by DigitalOcean', image: '/hatch-logo-black-v2.png', compact: true },
];

const FOOTER_COLUMNS = [
  ['Hotel Groups', 'Omnichannel Inbox', 'Multi-Property Operations', 'Channel Manager', 'Maintenance Coordination', 'Enterprise CRM', 'Revenue Management', 'Direct Booking Conversion', 'Room Upgrades', 'Performance Reporting', 'Conversational Analytics', 'See all Hotel Groups ->'],
  ['Independent Hotels', 'AI Concierge', 'Always-On Front Desk', 'After-Hours Receptionist', 'Guest Experience', 'Review Responses', 'Guest Feedback', 'Guest Memory (CRM)', 'Room Upgrades', 'See all Independent Hotels ->'],
  ['Short-Term Rentals', 'Vacation Rental Automation', 'Airbnb Automation', 'STR Automation Suite', '24/7 Guest Support', 'Smart Messaging', 'Channel Manager', 'Maintenance Coordination', 'Housekeeping Coordination', 'Property Management', 'Gap Night Fill', 'See all Short-Term Rentals ->'],
  ['DockClaw vs.', 'Canary', 'Akia', 'Revinate', 'Duve', 'Volara', 'Enso Connect', 'Besty AI', 'HiJiffy', 'Quicktext', 'Zendesk', 'Intercom'],
];

const isInternalHref = (href) => typeof href === 'string' && href.startsWith('/');

function NavHref({ href, className, onClick, children }) {
  if (isInternalHref(href)) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className={className} onClick={onClick} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function DockClawMark({ dark = false }) {
  return (
    <Link to="/" className={`paper-logo ${dark ? 'paper-logo-dark' : ''}`} aria-label="DockClaw home">
      <img src="/dlogoblack.svg" alt="DockClaw" className="paper-logo-full" />
    </Link>
  );
}

function ArrowButton({ children, to = '/register', light = false, showArrow = false }) {
  return (
    <Link to={to} className={light ? 'paper-button paper-button-light' : 'paper-button'}>
      <span>{children}</span>
      {showArrow ? <ArrowUpRight size={16} /> : null}
    </Link>
  );
}

function MaskedHeroTitle({ lines }) {
  const titleRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = titleRef.current;
    if (!node) return undefined;

    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.75) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: [0, 0.75] },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <h1 ref={titleRef} className={`paper-mask-title ${inView ? 'is-visible' : ''}`}>
      {lines.map((line, index) => (
        <span className="paper-line-mask" key={line}>
          <span style={{ '--line-index': index }}>{line}</span>
        </span>
      ))}
    </h1>
  );
}

function MaskedHeroParagraph({ lines }) {
  const paragraphRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = paragraphRef.current;
    if (!node) return undefined;

    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.75) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: [0, 0.75] },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <p ref={paragraphRef} className={`paper-mask-paragraph ${inView ? 'is-visible' : ''}`}>
      {lines.map((line, index) => (
        <span className="paper-line-mask" key={line}>
          <span style={{ '--line-index': index + 2 }}>{line}</span>
        </span>
      ))}
    </p>
  );
}

function SectionRule({ left, right, dark = false }) {
  return (
    <div className={`paper-rule ${dark ? 'paper-rule-dark' : ''}`}>
      <div>
        <span>{left}</span>
        {right ? <span>{right}</span> : null}
      </div>
      <i />
    </div>
  );
}

function HomeNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    if (!openDropdown) return undefined;

    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpenDropdown(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDropdown]);

  const dropdowns = {
    product: productDropdownLinks,
    resources: resourceDropdownLinks,
  };

  const renderDropdown = (key) => (
    <div className={`paper-nav-dropdown ${openDropdown === key ? 'is-open' : ''}`}>
      <div className="paper-nav-dropdown-panel">
        {dropdowns[key].map((link) => (
          <NavHref
            key={link.label}
            href={link.href}
            className="paper-nav-dropdown-link"
            onClick={() => setOpenDropdown(null)}
          >
            <span className="paper-nav-dropdown-icon">
              <ProductNavIcon type={link.icon} />
            </span>
            <span>
              <b>{link.label}</b>
              <small>{link.subtext}</small>
            </span>
          </NavHref>
        ))}
      </div>
    </div>
  );

  return (
    <header className="paper-nav" ref={navRef}>
      <div className="paper-nav-inner">
        <DockClawMark />
        <nav className="paper-nav-links" aria-label="Primary navigation">
          <div className="paper-nav-item">
            <button
              type="button"
              className="paper-nav-link"
              aria-expanded={openDropdown === 'product'}
              onClick={() => setOpenDropdown((value) => (value === 'product' ? null : 'product'))}
            >
              <span>Product</span>
              <ChevronDown size={12} />
            </button>
            {renderDropdown('product')}
          </div>
          <Link to="/workflows" className="paper-nav-link">Workflows</Link>
          <div className="paper-nav-item">
            <button
              type="button"
              className="paper-nav-link"
              aria-expanded={openDropdown === 'resources'}
              onClick={() => setOpenDropdown((value) => (value === 'resources' ? null : 'resources'))}
            >
              <span>Resources</span>
              <ChevronDown size={12} />
            </button>
            {renderDropdown('resources')}
          </div>
          <Link to="/pricing" className="paper-nav-link">Pricing</Link>
          <Link to="/contact" className="paper-nav-link">Contact</Link>
        </nav>
        <div className="paper-nav-actions">
          <Link to="/login" className="paper-nav-secondary">Sign In</Link>
          <Link to="/register" className="paper-nav-primary">Sign Up</Link>
          <button type="button" className="paper-mobile-toggle" aria-label="Toggle navigation" onClick={() => setMobileOpen((open) => !open)}>
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {mobileOpen ? (
        <div className="paper-mobile-menu">
          {[
            ['Product', productDropdownLinks],
            ['Resources', resourceDropdownLinks],
          ].map(([label, links]) => (
            <div className="paper-mobile-menu-group" key={label}>
              <strong>{label}</strong>
              {links.map((link) => (
                <NavHref key={link.label} href={link.href} onClick={() => setMobileOpen(false)}>
                  {link.label}
                </NavHref>
              ))}
            </div>
          ))}
          <Link to="/workflows" onClick={() => setMobileOpen(false)}>Workflows</Link>
          <Link to="/pricing" onClick={() => setMobileOpen(false)}>Pricing</Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)}>Contact</Link>
          <Link to="/login" onClick={() => setMobileOpen(false)}>Sign In</Link>
          <Link to="/register" onClick={() => setMobileOpen(false)}>Sign Up</Link>
        </div>
      ) : null}
    </header>
  );
}

export default function HomePage() {
  const [activeLearningStep, setActiveLearningStep] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveLearningStep((step) => (step + 1) % LEARNING_STEPS.length);
    }, 3600);

    return () => window.clearInterval(timer);
  }, []);

  const learningImage = LEARNING_STEPS[activeLearningStep][3];

  const renderLearningCard = (step, index) => {
    const [number, title, body] = step;
    const isActive = index === activeLearningStep;

    return (
      <article
        key={title}
        className={isActive ? 'selected' : ''}
        onMouseEnter={() => setActiveLearningStep(index)}
      >
        <span>{number}</span>
        <h3>{title}</h3>
        <p>{body}</p>
      </article>
    );
  };

  return (
    <>
      <SEOHead
        title="AI agents for hospitality"
        description="DockClaw elevates guest experience and automates internal operations across hospitality systems."
        keywords="hospitality AI agents, hotel automation, guest messaging, AI concierge, DockClaw"
        path="/"
        schema={organizationSchema()}
      />

      <div className="paper-page">
        <Header />

        <main id="main-content">
          <section className="paper-hero">
            <div className="paper-container">
              <div className="paper-hero-copy">
                <MaskedHeroTitle lines={['We help you run your company', 'with your next agentic stuff']} />
                <MaskedHeroParagraph lines={['Agentic teammates that automates workflows, helps to increase speed 10x', 'inside Telegram, Slack, and the tools your team already uses.']} />
                <ArrowButton>Hire Your First Staff Member</ArrowButton>
              </div>
            </div>
          </section>

          <section className="paper-visual paper-container" aria-label="Product preview">
            <div className="paper-preview">
              <img src={HERO_IMAGE} alt="" />
              <div className="paper-dashboard paper-chat-dashboard">
                <div className="paper-chat-left">
                  <div className="paper-chat-brand">
                    <DockClawMark />
                    <span>Dockagent</span>
                  </div>
                  <div className="paper-chat-room active">
                    <strong>Lead Scout</strong>
                    <p>Finding 40 qualified prospects in fintech.</p>
                    <span>12 leads ready</span>
                  </div>
                  <div className="paper-chat-room">
                    <strong>Research Agent</strong>
                    <p>Reading websites, LinkedIn pages, and funding notes.</p>
                    <span>Briefs updating</span>
                  </div>
                  <div className="paper-chat-room">
                    <strong>Outreach Agent</strong>
                    <p>Drafting cold emails in your writing style.</p>
                    <span>5 drafts ready</span>
                  </div>
                  <div className="paper-chat-room">
                    <strong>Finance Agent</strong>
                    <p>Checking spend, runway, and next best actions.</p>
                    <span>Report queued</span>
                  </div>
                  <div className="paper-chat-pills">
                    <span>Support</span>
                    <span>Reports</span>
                    <span>Ops</span>
                  </div>
                </div>

                <div className="paper-ai-conversation">
                  <div className="paper-ai-topbar">
                    <div>
                      <strong>Kyoka</strong>
                      <span>AI operator</span>
                    </div>
                    <em>working</em>
                  </div>
                  <div className="paper-ai-thread">
                    <div className="paper-ai-message user">
                      Find companies we should sell to, research each one, draft outreach, and send me a report.
                    </div>
                    <div className="paper-ai-message assistant">
                      Got it. I am assigning the work to your agentic staff.
                    </div>
                    <div className="paper-ai-workflow">
                      <span>Scout leads</span>
                      <span>Research context</span>
                      <span>Draft outreach</span>
                      <span>Prepare report</span>
                    </div>
                    <div className="paper-ai-message assistant final">
                      Done. I found the leads, wrote the drafts, and prepared your summary for approval.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="paper-trust paper-container">
            <p>supported by</p>
            <div>
              {TRUST_LOGOS.map((logo) => (
                typeof logo === 'string'
                  ? <span key={logo}>{logo}</span>
                  : (
                    <span
                      key={logo.label}
                      className={`paper-trust-image ${logo.darken ? 'paper-trust-image-darken' : ''} ${logo.compact ? 'paper-trust-image-compact' : ''} ${logo.large ? 'paper-trust-image-large' : ''}`}
                    >
                      <img src={logo.image} alt={logo.label} />
                    </span>
                  )
              ))}
            </div>
          </section>

          <section id="features" className="paper-section paper-container">
            <div className="paper-heading-row">
              <h2>Your personal agentic staff, to ship 10x faster</h2>
              <ArrowButton showArrow>Get Started</ArrowButton>
            </div>
            <div className="paper-product-grid">
              {PRODUCT_CARDS.map((card, index) => (
                <article className={`paper-product-card ${card.title === 'Your own agentic stuff' ? 'paper-agent-staff-card' : ''} ${card.title === 'Automate every workflow' ? 'paper-workflow-card' : ''} ${card.title === 'Reports in hand' ? 'paper-report-card' : ''}`} key={card.title}>
                  <div>
                    <span>{card.label}</span>
                    <h3>{card.title}</h3>
                    <p>{card.body}</p>
                  </div>
                  {card.title === 'Your own agentic stuff' ? (
                    <div className="paper-agent-staff-visual" aria-hidden="true">
                      <div className="paper-agent-staff-window">
                        <div className="paper-agent-staff-top">
                          <span>Dockagent staff</span>
                          <i>6 active</i>
                        </div>
                        <div className="paper-agent-staff-list">
                          {[
                            ['Lead Scout', 'finds qualified prospects', 'Researching 42 companies'],
                            ['Research Agent', 'builds account briefs', 'Pulling market signals'],
                            ['Outreach Agent', 'drafts cold emails', 'Writing in your tone'],
                            ['Support Triage', 'routes customer issues', 'Sorting priority tickets'],
                            ['Ops Agent', 'runs repeatable workflows', 'Checking handoff steps'],
                            ['Finance Agent', 'tracks spend and runway', 'Preparing weekly notes'],
                          ].map(([name, role, note], agentIndex) => (
                            <div key={name} style={{ '--staff-delay': `${agentIndex * 0.18}s` }}>
                              <strong>{name}</strong>
                              <p>{role}</p>
                              <span>{note}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : card.title === 'Automate every workflow' ? (
                    <div className="paper-workflow-visual" aria-hidden="true">
                      <div className="paper-workflow-chat">
                        <div className="paper-workflow-chat-top">
                          <span>Dockagent</span>
                          <i>Automating</i>
                        </div>
                        <div className="paper-workflow-messages">
                          <p className="paper-workflow-user">Can you automate my daily follow-ups and task handoffs?</p>
                          <p className="paper-workflow-ai">Yes. I’ll check your inbox, update the CRM, draft replies, and prepare the next actions.</p>
                          <div className="paper-workflow-stack">
                            <span>Read new leads</span>
                            <span>Update pipeline</span>
                            <span>Draft follow-ups</span>
                            <span>Send daily summary</span>
                          </div>
                          <p className="paper-workflow-ai">Workflow is ready. You approve once, then I run it every morning.</p>
                        </div>
                      </div>
                    </div>
                  ) : card.title === 'Reports in hand' ? (
                    <div className="paper-report-visual" aria-hidden="true">
                      <div className="paper-report-window">
                        <div className="paper-report-top">
                          <span>Reports agent</span>
                          <i>Weekly view</i>
                        </div>
                        <div className="paper-report-chat">
                          <p className="paper-report-user">Show lead performance this week.</p>
                          <p className="paper-report-ai">Here is the channel report generated from your active workflows.</p>
                        </div>
                        <div className="paper-report-chart">
                          {[
                            ['Email sent', '84%', '84'],
                            ['Voice sent', '58%', '31'],
                            ['Website words', '72%', '12.4k'],
                            ['Phone-to-text', '46%', '19'],
                          ].map(([label, width, value]) => (
                            <div className="paper-report-row" key={label}>
                              <span>{label}</span>
                              <div><i style={{ width }} /></div>
                              <strong>{value}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </section>

          <section className="paper-section paper-container">
            <div className="paper-heading-row paper-workflows-heading">
              <div className="paper-section-heading">
                <h2>Workflows you can build today</h2>
                <p>Start with practical workflows your AI staff can run across your tools.</p>
              </div>
              <ArrowButton to="/use-cases" showArrow>Check workflows</ArrowButton>
            </div>
            <div className="paper-capability-grid">
              {CAPABILITY_CARDS.map(([title, body, image]) => (
                <article key={title} className={`paper-capability-card ${title === 'Lead Scout' ? 'paper-capability-card-lead' : ''} ${title === 'Outreach Agent' ? 'paper-capability-card-outreach' : ''}`}>
                  <div>
                    {title === 'Lead Scout' ? (
                      <div className="paper-lead-scout-animation" aria-hidden="true">
                        <div className="paper-lead-scan-orb">
                          <i />
                          <span />
                        </div>
                        <div className="paper-lead-stream">
                          {[
                            ['Apex Cloud', 'SaaS', '92'],
                            ['Nova Ventures', 'Fintech', '85'],
                            ['Bluepeak Systems', 'IT Services', '78'],
                          ].map(([company, industry, score]) => (
                            <div key={company}>
                              <strong>{company}</strong>
                              <span>{industry}</span>
                              <em>{score}</em>
                            </div>
                          ))}
                        </div>
                        <div className="paper-lead-match">
                          <small>Qualified lead found</small>
                          <strong>Apex Cloud</strong>
                          <span>92 match score</span>
                        </div>
                      </div>
                    ) : title === 'Outreach Agent' ? (
                      <div className="paper-outreach-animation" aria-hidden="true">
                        <div className="paper-outreach-mail">
                          <span className="paper-outreach-graph">
                            <svg viewBox="0 0 140 74" aria-hidden="true" preserveAspectRatio="none">
                              <defs>
                                <linearGradient id="paper-outreach-gradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#d66528" stopOpacity="0.24" />
                                  <stop offset="100%" stopColor="#d66528" stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              <path className="paper-outreach-area" d="M0 66 L32 52 L58 57 L90 31 L140 10 L140 74 L0 74 Z" />
                              <path className="paper-outreach-line" d="M0 66 L32 52 L58 57 L90 31 L140 10" />
                            </svg>
                          </span>
                          <strong>Campaign ready</strong>
                          <p>342 personalized emails queued</p>
                        </div>
                        <div className="paper-outreach-list">
                          {[
                            ['Maya Chen', 'Apex Cloud', 'Send'],
                            ['Noah Patel', 'Nova Ventures', 'Follow up'],
                            ['Lina Brooks', 'Bluewave', 'Send'],
                            ['Owen Reed', 'Northpeak', 'Follow up'],
                          ].map(([name, company, action], companyIndex) => (
                            <p key={name} style={{ '--outreach-delay': `${companyIndex * 0.22}s` }}>
                              <span><strong>{name}</strong>{company}</span>
                              <em>{action}</em>
                            </p>
                          ))}
                        </div>
                      </div>
                    ) : title === 'Research Assistant' ? (
                      <div className="paper-research-chat" aria-hidden="true">
                        <div className="paper-research-chat-window">
                          <div className="paper-research-chat-top">
                            <span>Research Assistant</span>
                            <i>AI</i>
                          </div>
                          <p className="paper-research-user">Help me research where Bitcoin can be in the next five weeks.</p>
                          <div className="paper-research-thinking">
                            <div className="paper-research-thinking-head">
                              <span>Thinking...</span>
                            </div>
                            <div className="paper-research-thinking-stream">
                              <p>Checking Bitcoin momentum, ETF flows, and five-week market structure.</p>
                              <p>Comparing PTC movement against BTC dominance and risk appetite.</p>
                              <p>Estimating likely upside range and the conditions needed to reach it.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : title === 'Support Triage' ? (
                      <div className="paper-support-bot" aria-hidden="true">
                        <div className="paper-support-window">
                          <div className="paper-support-bot-head">
                            <span>Support bot</span>
                            <i>Online</i>
                          </div>
                          <p className="paper-support-user">I need help with my account setup.</p>
                          <p className="paper-support-agent">I can help. I found the missing setup step in your workspace.</p>
                          <p className="paper-support-agent">Connect your inbox and I’ll finish the routing rules for you.</p>
                        </div>
                      </div>
                    ) : title === 'A Virtual Assistant' ? (
                      <div className="paper-virtual-assistant" aria-hidden="true">
                        <div className="paper-virtual-phone">
                          <div className="paper-virtual-top">
                            <span>Dockagent</span>
                            <i>Today</i>
                          </div>
                          <p className="paper-virtual-message">
                            Today’s to-do list:
                          </p>
                          <div className="paper-virtual-stats">
                            <span>4 meetings</span>
                            <span>6 follow-ups</span>
                          </div>
                          <div className="paper-virtual-checks">
                            <span>Review calendar</span>
                            <span>Prep meeting notes</span>
                            <span>Send reminders</span>
                          </div>
                        </div>
                      </div>
                    ) : title === 'Finance Assistant' ? (
                      <div className="paper-finance-assistant" aria-hidden="true">
                        <div className="paper-finance-card">
                          <div className="paper-finance-top">
                            <span>Portfolio allocation</span>
                            <i>Live plan</i>
                          </div>
                          <div className="paper-finance-summary">
                            <strong>$128.4k</strong>
                            <span>Projected runway +18%</span>
                          </div>
                          <div className="paper-finance-chart">
                            {[
                              ['Cash', '22%'],
                              ['Growth', '38%'],
                              ['AI tools', '18%'],
                              ['Ops', '22%'],
                            ].map(([label, value]) => (
                              <p key={label}>
                                <span>{label}</span>
                                <strong>{value}</strong>
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="paper-capability-blank" aria-hidden="true" />
                    )}
                    <h3>{title}</h3>
                  </div>
                  <p>{body}</p>
                </article>
              ))}
            </div>
          </section>

          <section id="how-it-works" className="paper-learn">
            <div className="paper-container">
              <div className="paper-heading-row paper-heading-row-dark">
                <h2>Agents that learn the way your business runs</h2>
                <div>
                  <p>Dockagent learns your behaviors and tailors to your business.</p>
                  <ArrowButton light>Hire Your First Staff Member</ArrowButton>
                </div>
              </div>
              <div className="paper-learn-grid">
                <div className="paper-learn-column">
                  {LEARNING_STEPS.slice(0, 2).map((step, index) => renderLearningCard(step, index))}
                </div>
                <div className="paper-learn-image">
                  <img key={learningImage} src={learningImage} alt="" />
                </div>
                <div className="paper-learn-column">
                  {LEARNING_STEPS.slice(2).map((step, index) => renderLearningCard(step, index + 2))}
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 4.8: Pricing (real V2 plans). id="pricing" is an anchor
              target (e.g. IntegrationPage links /#pricing). */}
          <section id="pricing" className="bg-[#0E0E0E]">
            <div className="mx-auto w-full max-w-7xl px-6 py-12 sm:px-8 sm:py-16">
              <div className="mx-auto flex max-w-[640px] flex-col items-center gap-3 text-center">
                <h2 className="text-balance text-[32px] font-medium leading-[1.1] tracking-[-0.015em] text-white sm:text-[40px]">
                  Simple pricing. Real staff.
                </h2>
                <p className="text-[15px] leading-[1.5] text-white/70">
                  Start with one staff member. Scale across your team without scaling headcount.
                </p>
              </div>

              <div className="mt-12">
                <V2PricingCards />
              </div>
            </div>
          </section>

          <section className="paper-section paper-customers">
            <div className="paper-container">
              <div className="paper-heading-row">
                <h2>Latest from the Dockclaw Blogs</h2>
                <ArrowButton to="/blog" showArrow>Our Blogs</ArrowButton>
              </div>
            </div>
            <div className="paper-customer-rail paper-customer-grid-static">
              {BLOG_CARDS.slice(0, 4).map(([title, excerpt, href, image]) => (
                <Link key={title} to={href} className="paper-customer-card paper-blog-card">
                  <div>
                    <img src={image} alt={title} />
                  </div>
                  <span>BLOG</span>
                  <h3>{title}</h3>
                  <p>{excerpt}</p>
                </Link>
              ))}
            </div>
          </section>

          <PaperCta />
        </main>

        <Footer />
      </div>
    </>
  );
}
